import {
  CONTENT_VERSION,
  SAVE_SCHEMA_VERSION,
  defaultPayload,
  type GuestPayload,
  type GuestSaveEnvelope,
} from './types';

const PRIMARY_KEY = 'project-roach.save.primary';
const BACKUP_KEY = 'project-roach.save.backup';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function canonicalEnvelope(envelope: Omit<GuestSaveEnvelope, 'checksum'>): string {
  return JSON.stringify({
    schemaVersion: envelope.schemaVersion,
    contentVersion: envelope.contentVersion,
    revision: envelope.revision,
    updatedAt: envelope.updatedAt,
    payload: envelope.payload,
  });
}

function normalizedPayload(payload: Partial<GuestPayload>, forceLeftHanded = false): GuestPayload {
  const defaults = defaultPayload();
  return {
    ...defaults,
    ...payload,
    settings: {
      ...defaults.settings,
      ...payload.settings,
      leftHanded: forceLeftHanded ? true : payload.settings?.leftHanded ?? defaults.settings.leftHanded,
    },
  };
}

export function checksum(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export class GuestSaveManager {
  constructor(private readonly storage: StorageLike) {}

  load(): GuestSaveEnvelope {
    const primary = this.parse(this.storage.getItem(PRIMARY_KEY));
    if (primary) return primary;

    const backup = this.parse(this.storage.getItem(BACKUP_KEY));
    if (backup) {
      this.storage.setItem(PRIMARY_KEY, JSON.stringify(backup));
      return backup;
    }

    return this.create(defaultPayload(), 0);
  }

  save(update: (payload: GuestPayload) => GuestPayload): GuestSaveEnvelope {
    const current = this.load();
    const nextPayload = update(structuredClone(current.payload));
    const next = this.create(nextPayload, current.revision + 1);
    const currentRaw = this.storage.getItem(PRIMARY_KEY);
    if (currentRaw && this.parse(currentRaw)) this.storage.setItem(BACKUP_KEY, currentRaw);
    this.storage.setItem(PRIMARY_KEY, JSON.stringify(next));
    return next;
  }

  clearCheckpoint(): GuestSaveEnvelope {
    return this.save((payload) => ({ ...payload, checkpoint: null }));
  }

  private create(payload: GuestPayload, revision: number): GuestSaveEnvelope {
    const unsigned = {
      schemaVersion: SAVE_SCHEMA_VERSION,
      contentVersion: CONTENT_VERSION,
      revision,
      updatedAt: Date.now(),
      payload,
    };
    return { ...unsigned, checksum: checksum(canonicalEnvelope(unsigned)) };
  }

  private parse(raw: string | null): GuestSaveEnvelope | null {
    if (!raw) return null;
    try {
      const candidate = JSON.parse(raw) as GuestSaveEnvelope;
      if (candidate.schemaVersion === 3 && candidate.contentVersion === CONTENT_VERSION && candidate.payload) {
        return this.create(normalizedPayload(candidate.payload), candidate.revision + 1);
      }
      if (candidate.schemaVersion === 2 && candidate.contentVersion === CONTENT_VERSION && candidate.payload) {
        return this.create(normalizedPayload(candidate.payload, true), candidate.revision + 1);
      }
      if (candidate.schemaVersion === 1 && candidate.contentVersion === 1 && candidate.payload) {
        const migrated = this.create({
          ...normalizedPayload(candidate.payload, true),
          checkpoint: candidate.payload.checkpoint ? { ...candidate.payload.checkpoint, levelId: candidate.payload.checkpoint.levelId?.startsWith('mic-') ? candidate.payload.checkpoint.levelId : 'mic-01-chai-drill' } : null,
        }, candidate.revision + 1);
        return migrated;
      }
      if (
        candidate.schemaVersion !== SAVE_SCHEMA_VERSION || candidate.contentVersion !== CONTENT_VERSION ||
        !Number.isInteger(candidate.revision) ||
        typeof candidate.updatedAt !== 'number' ||
        !candidate.payload ||
        typeof candidate.checksum !== 'string'
      ) {
        return null;
      }
      const { checksum: storedChecksum, ...unsigned } = candidate;
      return checksum(canonicalEnvelope(unsigned)) === storedChecksum ? candidate : null;
    } catch {
      return null;
    }
  }
}

const fallbackStorage: StorageLike = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

export const guestSaveManager = new GuestSaveManager(typeof window === 'undefined' ? fallbackStorage : window.localStorage);
